import { HStack, Stack, Text } from '@chakra-ui/react'
import { DARK_TEXT_STEEL } from 'constants/dark-tokens'
import SwitcherSelect from './switcher-select'
import { SwitcherOrg, SwitcherTeam } from './use-org-team-tree'

// "SWITCH TEAM  [organization ▾] [team ▾]" — the scope control under the banner.
//
// One label everywhere. It read "Switch" on the Team-scoped pages, which said less
// than the same control says on the dashboard for no reason the viewer could see.
//
// Fully controlled: the dashboard owns the selection so the mobile chip filter can
// drive the same state. This never touches the globally active organization —
// looking at another org's banner is not the same as switching the app into it.
//
// md+: row geometry is fixed by the design at 434 × 28: 86 (label) + 8 + 166 + 8
// + 166. Nothing grows to fill, which is why it can sit flush-right under a banner
// of any width. Phone: the label sits above and the two selects split the width.

/** Sentinel for the "all" option — `null` selection cannot round-trip through a string value. */
const ALL = ''

const ROW_HEIGHT = '28px'

interface IOrgTeamSwitcherProps {
  organizations: SwitcherOrg[]
  /** null = "All organizations". */
  selectedOrgId: number | null
  onOrgChange: (orgId: number | null) => void
  /** Teams of the selected org; empty while no org is picked. */
  teams: SwitcherTeam[]
  /** null = "All teams". */
  selectedTeamId: number | null
  onTeamChange: (teamId: number | null) => void
  isLoading?: boolean
  /**
   * Offer the "All organizations" row. True for the dashboard, which aggregates
   * across everything; false for pages whose every request names one organization.
   * The "All teams" row is always offered — it is the union across the selected
   * organization's Teams, which every consumer can render.
   */
  allowAllOrgs?: boolean
}

const OrgTeamSwitcher = ({
  organizations,
  selectedOrgId,
  onOrgChange,
  teams,
  selectedTeamId,
  onTeamChange,
  isLoading,
  allowAllOrgs = true
}: IOrgTeamSwitcherProps) => {
  const orgOptions = [
    ...(allowAllOrgs ? [{ value: ALL, label: 'All organizations' }] : []),
    ...organizations.map(o => ({ value: String(o.id), label: o.name }))
  ]

  const teamOptions = [{ value: ALL, label: 'All teams' }, ...teams.map(t => ({ value: String(t.id), label: t.name }))]

  return (
    <Stack
      direction={{ base: 'column', md: 'row' }}
      spacing="8px"
      justify="flex-end"
      align={{ base: 'stretch', md: 'center' }}
      h={{ base: 'auto', md: ROW_HEIGHT }}
    >
      <Text
        fontFamily="body"
        fontWeight={600}
        fontSize="12px"
        lineHeight="18px"
        letterSpacing="0.02em"
        textTransform="uppercase"
        color={DARK_TEXT_STEEL}
        m={0}
        flexShrink={0}
      >
        Switch team
      </Text>

      <HStack spacing="8px" w={{ base: '100%', md: 'auto' }}>
        <SwitcherSelect
          value={selectedOrgId === null ? ALL : String(selectedOrgId)}
          options={orgOptions}
          onChange={value => onOrgChange(value === ALL ? null : Number(value))}
          placeholder="All organizations"
          isDisabled={isLoading}
        />

        <SwitcherSelect
          value={selectedTeamId === null ? ALL : String(selectedTeamId)}
          options={teamOptions}
          onChange={value => onTeamChange(value === ALL ? null : Number(value))}
          placeholder="All teams"
          // Nothing to choose from until an organization narrows the list.
          isDisabled={isLoading || selectedOrgId === null}
        />
      </HStack>
    </Stack>
  )
}

export default OrgTeamSwitcher
